import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap, switchMap, catchError } from 'rxjs/operators';
import { LoginModel } from '../shared/login.model';
import { environment } from '../../../environments/environment';
import { Role, UserAccountClient, UserInfoDTO, UserTokenDTO } from '../models/models.swagger.generated';
import { TranslateService } from '@ngx-translate/core';
import { LoginProxy } from '../http/login-proxy.service';
import { Idle } from '@ng-idle/core';

import { SyncfusionTranslationService } from './syncfusion-translation.service';
import { Settings } from 'luxon';
import { ToastService } from './toast.service';

export enum AuthStatus {
  success,
  twoFACodeRequired
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  // store the URL so we can redirect after logging in
  public redirectUrl: string;

  // private userInfoModel: InfoBindingModel;
  private userInfoDTO: UserInfoDTO;

  constructor(
    private loginProxy: LoginProxy,
    private userAccountClient: UserAccountClient,
    private readonly translate: TranslateService,
    private readonly toastService: ToastService,
    private idle: Idle,
    private readonly syncfusionTranslationService: SyncfusionTranslationService,
    @Inject(DOCUMENT) private document: Document) {
    this.resetLocalStorageIfNecessary();
    // Reset user info from the local storage
    if (localStorage[environment.localStorageUserInfo]) {
      this.userInfoDTO = UserInfoDTO.fromJS(JSON.parse(localStorage[environment.localStorageUserInfo]));
      this.setClientLanguage(this.userInfoDTO.language);
      setTimeout(() => this.idle.watch(), 1000);
    }
  }

  public login(login: LoginModel): Observable<AuthStatus> {

    return this.loginProxy.login(login.username, login.password, login.twoFactorAuthenticationToken ?? null).pipe(
      switchMap((res: UserTokenDTO) => {
        if (res.twoFactorAuthCodeRequired) {
          return [AuthStatus.twoFACodeRequired];
        }

        // login successful if there's a jwt token in the response
        if (res.access_token) {
          localStorage[environment.localStorageToken] = res.access_token;
          this.idle.watch();
          this.updateActivityTimeStamp();
        }
        if (res.refresh_token) {
          localStorage[environment.localStorageRefreshToken] = res.refresh_token;
        }

        // get current user info to get the user's roles
        return this.userAccountClient.info().pipe(
          catchError((error) => {
            this.resetLocalStorage();
            throw error;
          }),
          map((userInfo: UserInfoDTO) => {
            this.setUserInfoDTO(userInfo);

            if (userInfo.shouldUseTwoFactorAuth) {
              this.toastService.showWarning('i18n.core.messages.should-setup-2fa-warning.title', 'i18n.core.messages.should-setup-2fa-warning.message');
            }

            return AuthStatus.success;
          })
        );
      })
    );
  }

  public logout(): void {
    this.userAccountClient.logout().subscribe();
    setTimeout(() => {
      this.resetLocalStorage();
      location.reload();
    }, 0);
  }

  public setUserLanguage(language: string) {
    const userInfo = this.getUserInfo();
    if (userInfo == null) {
      this.setClientLanguage(language);
      return;
    }
    userInfo.language = language;
    this.setUserInfoDTO(userInfo);
    this.userAccountClient.setUserLanguage(language).subscribe();
  }

  public isUserLoggedIn(): boolean {
    return !!localStorage[environment.localStorageToken];
  }

  public isUserAdmin(): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }

    if (!this.userInfoDTO || !this.userInfoDTO.roles) {
      return false;
    }

    return this.userInfoDTO.roles.indexOf(Role.Admin) > -1;
  }

  public isUserAdminOrClientAdmin(): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }

    if (!this.userInfoDTO || !this.userInfoDTO.roles) {
      return false;
    }

    return this.userInfoDTO.roles.indexOf(Role.Admin) > -1
      || this.userInfoDTO.roles.indexOf(Role.ClientAdmin) > -1;
  }

  public isUserAdminOrClientAdminOrCompanyAdmin(): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }

    if (!this.userInfoDTO || !this.userInfoDTO.roles) {
      return false;
    }

    return this.userInfoDTO.roles.indexOf(Role.Admin) > -1
      || this.userInfoDTO.roles.indexOf(Role.ClientAdmin) > -1
      || this.userInfoDTO.roles.indexOf(Role.CompanyAdmin) > -1;;
  }

  public hasUserClientAssigned(): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }
    return !!this.userInfoDTO.client;
  }

  public isUserClientUser(): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }
    const hasClientUserRole = this.userInfoDTO.roles.indexOf(Role.ClientUser) > -1;
    return this.userInfoDTO.client && hasClientUserRole;
  }

  public isUserStandardUser(): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }

    if (!this.userInfoDTO || !this.userInfoDTO.roles) {
      return false;
    }

    return this.userInfoDTO.roles.indexOf(Role.User) > -1;
  }

  public isUserCompanyAdmin(): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }

    if (!this.userInfoDTO || !this.userInfoDTO.roles) {
      return false;
    }

    return this.userInfoDTO.roles.indexOf(Role.CompanyAdmin) > -1;;
  }

  public getUserInfo(): UserInfoDTO {
    if (!this.isUserLoggedIn() || !localStorage[environment.localStorageUserInfo]) {
      return null;
    }

    this.userInfoDTO = UserInfoDTO.fromJS(JSON.parse(localStorage[environment.localStorageUserInfo]));

    return this.userInfoDTO;
  }

  public loadUserInfoDTO(): Observable<UserInfoDTO> {
    return this.userAccountClient.info().pipe(tap(userInfo => {
      this.setUserInfoDTO(userInfo);
    }));
  }

  public updateActivityTimeStamp(): void {
    if (this.isUserLoggedIn()) {
      localStorage[environment.localStorageActivityTimestamp] = Date.now;
    }
  }

  private setUserInfoDTO(userInfo: UserInfoDTO) {
    if (!userInfo) {
      return;
    }
    this.userInfoDTO = userInfo;
    localStorage[environment.localStorageUserInfo] = JSON.stringify(userInfo);
    this.setClientLanguage(userInfo.language);
  }

  private setClientLanguage(language: string) {
    language = language ?? 'de';
    Settings.defaultLocale = language;
    this.syncfusionTranslationService.use(language);
    this.translate.use(language);
    if (environment.allowBrowserTranslation) {
      this.document.documentElement.lang = language;
    } else {
      this.document.documentElement.translate = false;
    }
  }

  private resetLocalStorageIfNecessary() {
    if (environment.production) {
      const timestamp = localStorage[environment.localStorageActivityTimestamp];
      if (!timestamp || timestamp + (environment.idleTimeoutInSeconds * 1000) < Date.now) {
        this.resetLocalStorage();
      }
    }

    // avoid possible dead lock
    if ((localStorage[environment.localStorageUserInfo] && !localStorage[environment.localStorageToken])
      || (!localStorage[environment.localStorageUserInfo] && localStorage[environment.localStorageToken])) {
      this.resetLocalStorage();
    }
  }

  private resetLocalStorage() {
    localStorage.removeItem(environment.localStorageToken);
    localStorage.removeItem(environment.localStorageRefreshToken);
    localStorage.removeItem(environment.localStorageUserInfo);
  }
}
